import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

function formatTimeRange(startTimeStr, endTimeStr) {
  const s = new Date(startTimeStr);
  const e = new Date(endTimeStr);
  const weekday = s.toLocaleString('en-US', { weekday: 'short' });
  const month = s.toLocaleString('en-US', { month: 'short' });
  const day = s.getDate();
  const startTime = s.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  const endTime = e.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  if (s.getDate() !== e.getDate()) {
    const eMonth = e.toLocaleString('en-US', { month: 'short' });
    return `${weekday}, ${month} ${day} ${startTime} – ${eMonth} ${e.getDate()} ${endTime}`;
  }
  return `${weekday}, ${month} ${day} • ${startTime} – ${endTime}`;
}

const CANCEL_LEAD_HOURS = 24 * 7; // one week

function LookupModal({ onClose }) {
  const [code, setCode] = useState('');
  const [results, setResults] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cancellingId, setCancellingId] = useState(null);
  const [cancelError, setCancelError] = useState({});

  useEffect(() => {
    axios.get(`${API_URL}/departments`)
      .then(res => setDepartments(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResults(null);
    if (!code.trim()) { setError('Please enter your lookup code.'); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/timeslots/lookup`, {
        params: { code: code.trim().toUpperCase() }
      });
      setResults(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Lookup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getDeptName = (id) => departments.find(d => d.id === id)?.name || 'Unknown';

  const hoursUntilStart = (slotStart) => {
    if (!slotStart) return Infinity;
    return (new Date(slotStart) - new Date()) / 3600000;
  };

  // Convert "2026-07-09T14:00:00" to "20260709T140000" (floating local time
   // — calendar app interprets in the user's local zone, matching convention assumption).
  const toIcsDate = (isoStr) => isoStr.replace(/[-:]/g, '').slice(0, 15);

  const escapeIcs = (text) =>
    String(text ?? '')
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\r?\n/g, '\\n');

  const handleDownloadCalendar = () => {
    if (!results?.signups?.length) return;
    const stamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d+/, '').slice(0, 15) + 'Z';
    const lines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Shore Leave Volunteer//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH'
    ];
    for (const s of results.signups) {
      const start = s.actual_start_time || s.slot_start;
      const end = s.actual_end_time || s.slot_end;
      if (!start || !end) continue;
      const dept = getDeptName(s.department_id);
      lines.push('BEGIN:VEVENT');
      lines.push(`UID:signup-${s.id}@shore-leave-volunteer.com`);
      lines.push(`DTSTAMP:${stamp}`);
      lines.push(`DTSTART:${toIcsDate(start)}`);
      lines.push(`DTEND:${toIcsDate(end)}`);
      lines.push(`SUMMARY:${escapeIcs('Volunteer: ' + dept)}`);
      if (s.notes) lines.push(`DESCRIPTION:${escapeIcs(s.notes)}`);
      lines.push('LOCATION:Shore Leave Convention');
      lines.push('END:VEVENT');
    }
    lines.push('END:VCALENDAR');
    const ics = lines.join('\r\n');
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'shore-leave-shifts.ics';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCancel = async (signup) => {
    const dept = getDeptName(signup.department_id);
    const when = formatTimeRange(signup.slot_start, signup.slot_end);
    if (!window.confirm(`Cancel your ${dept} shift on ${when}?`)) return;

    setCancellingId(signup.id);
    setCancelError(prev => ({ ...prev, [signup.id]: '' }));
    try {
      await axios.post(`${API_URL}/timeslots/lookup/cancel`, {
        code: code.trim().toUpperCase(),
        signup_id: signup.id
      });
      // Remove the cancelled signup from the local view
      setResults(prev => ({
        ...prev,
        signups: prev.signups.filter(s => s.id !== signup.id)
      }));
    } catch (err) {
      console.error('Cancel failed:', err);
      const data = err.response?.data;
      const status = err.response?.status;
      let msg;
      if (data?.message) msg = data.message;
      else if (data?.error) msg = data.error;
      else if (status) msg = `Could not cancel (HTTP ${status}). Please try again.`;
      else msg = 'Could not reach the server. Please check your connection.';
      setCancelError(prev => ({ ...prev, [signup.id]: msg }));
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <div className="lookup-modal-overlay" onClick={onClose}>
      <div className="lookup-modal" onClick={e => e.stopPropagation()}>

        <div className="lookup-modal-header">
          <h3 className="lookup-modal-title">My Shifts</h3>
          <button className="lookup-modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <p className="lookup-modal-subtitle">Enter your 5-character volunteer code.</p>

        <form className="lookup-form" onSubmit={handleSubmit}>
          <input
            className="lookup-input lookup-code-input"
            type="text"
            placeholder="AB3K9"
            maxLength={5}
            value={code}
            onChange={e => { setCode(e.target.value.toUpperCase()); setResults(null); setError(''); }}
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            autoFocus
          />
          <button className="lookup-btn" type="submit" disabled={loading}>
            {loading ? 'Searching…' : 'Look Up'}
          </button>
        </form>

        <p className="lookup-recovery">
          Forgot your code? Email{' '}
          <a href="mailto:ccole@shore-leave.com">ccole@shore-leave.com</a>{' '}
          to recover it.
        </p>

        {error && <p className="lookup-error">{error}</p>}

        {results && results.signups?.length === 0 && (
          <p className="lookup-empty">No signups found for that code.</p>
        )}

        {results && results.signups?.length > 0 && (
          <div className="lookup-person">
            <div className="lookup-person-name">{results.volunteer.name}</div>
            <HoursBar signups={results.signups} />
            <button className="lookup-calendar-btn" onClick={handleDownloadCalendar}>
              📅 Add Shifts to Calendar
            </button>
            <div className="lookup-cards">
              {results.signups
                .sort((a, b) => new Date(a.slot_start) - new Date(b.slot_start))
                .map(signup => {
                  const start = signup.actual_start_time || signup.slot_start;
                  const end = signup.actual_end_time || signup.slot_end;
                  const hrsUntil = hoursUntilStart(signup.slot_start);
                  const tooLate = hrsUntil < CANCEL_LEAD_HOURS;
                  const alreadyStarted = hrsUntil <= 0;
                  return (
                    <div key={signup.id} className="lookup-card">
                      <div className="lookup-card-main">
                        <div className="lookup-card-dept">
                          {getDeptName(signup.department_id)}
                          {signup.double_time && <span className="lookup-double-badge">⭐ 2×</span>}
                        </div>
                        <div className="lookup-card-time">
                          {start && end ? formatTimeRange(start, end) : '—'}
                        </div>
                        {signup.notes && (
                          <div className="lookup-card-notes">{signup.notes}</div>
                        )}
                        {cancelError[signup.id] && (
                          <div className="lookup-card-error">{cancelError[signup.id]}</div>
                        )}
                      </div>
                      {!alreadyStarted && (
                        <button
                          className={`lookup-cancel-btn ${tooLate ? 'disabled' : ''}`}
                          onClick={() => !tooLate && handleCancel(signup)}
                          disabled={tooLate || cancellingId === signup.id}
                          title={tooLate ? 'Contact a leader to cancel within one week of your shift' : 'Cancel this shift'}
                        >
                          {cancellingId === signup.id
                            ? 'Cancelling…'
                            : tooLate
                              ? 'Contact leader to cancel'
                              : 'Cancel'}
                        </button>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

const HOUR_GOAL = 12;

function HoursBar({ signups }) {
  const totalHours = signups.reduce((sum, signup) => {
    const start = signup.slot_start;
    const end = signup.slot_end;
    if (!start || !end) return sum;
    const hours = (new Date(end) - new Date(start)) / 3600000;
    return sum + (signup.double_time ? hours * 2 : hours);
  }, 0);

  const rounded = Math.round(totalHours * 10) / 10;
  const pct = Math.min((rounded / HOUR_GOAL) * 100, 100);

  return (
    <div className="lookup-hours-bar">
      <div className="lookup-hours-label">
        <span className="lookup-hours-count">{rounded} out of {HOUR_GOAL} hours</span>
      </div>
      <div className="lookup-hours-track">
        <div
          className={`lookup-hours-fill ${pct >= 100 ? 'hours-full' : pct >= 66 ? 'hours-high' : pct >= 33 ? 'hours-mid' : 'hours-low'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export default LookupModal;
